import React, { useReducer, useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Space, Form, Input, Select, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import throttle from 'lodash/throttle';

import {
  Table,
  basicSort,
  generateFiltersForGenericColumn,
  onFilterGenericColumn,
  ApplicationUIContext,
} from '@yuhuinc/ui-library';

import { searchApplicants } from '../store/actions';

import { getTranslatedString } from 'helpers/i18n';

const DELIMETER = '__';

const ALL_OPTION = {
  label: getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.all'),
  value: 'all',
};

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const StyledSpace = styled(Space)`
  > * {
    flex: 1;
  }
`;

const throttledSearchApplicants = throttle(searchApplicants, 300, { trailing: false });

function extractIdFromValue(value) {
  return value.split(DELIMETER).pop();
}

const initialState = {
  fetchingFoundApplicants: false,
  foundApplicants: [],
  selectedApplicant: undefined,
};

function reducer(state, [action, payload]) {
  switch (action) {
    case 'SET_FETCHING':
      return { ...state, fetchingFoundApplicants: payload };
    case 'SET_FOUND_APPLICANTS':
      return { ...state, fetchingFoundApplicants: false, foundApplicants: payload };
    case 'SET_SELECTED_APPLICANT':
      return { ...state, selectedApplicant: payload };
    default:
      return state;
  }
}

function SearchApplicants({
  mode,
  siteAndProperties,
  initialValues,
  extraFormItems = null,
  onSelect,
  onCancel,
}) {
  const { isMobile } = useContext(ApplicationUIContext);
  const applicationForm = Form.useFormInstance();
  const applicationSelectedAdditionalApplicants =
    applicationForm.getFieldValue('additional_applicants') || [];
  const applicationSelectedLeadApplicant = applicationForm.getFieldValue('selectedApplicant');
  const applicationSelectedApplicants = [
    applicationSelectedLeadApplicant,
    ...applicationSelectedAdditionalApplicants,
  ];
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [filteredInfo, setFilteredInfo] = useState(null);
  const [sortedInfo, setSortedInfo] = useState(null);
  const [{ fetchingFoundApplicants, foundApplicants, selectedApplicant }, dispatch] = useReducer(
    reducer,
    initialState
  );
  const selectedSite = siteAndProperties.find((site) => site.key === values?.site);
  const sitesOptions = siteAndProperties.map((site) => ({ label: site.name, value: site.key }));
  const propertiesOptions = selectedSite
    ? selectedSite.properties.map((property) => ({ label: property.name, value: property.key }))
    : [];
  const applicantsDataSource = foundApplicants
    .filter((applicant) => {
      if (mode === 'add_additional_applicant') {
        return !applicationSelectedApplicants.some(
          (selectedAdditionalApplicant) => selectedAdditionalApplicant.email === applicant.email
        );
      }

      return applicant;
    })
    .map((applicant) => ({
      key: applicant.id,
      id: applicant.id,
      erp_ids: applicant.erp_ids,
      email: applicant.email,
      full_name: applicant.full_name,
      first_name: applicant.first_name,
      last_name: applicant.last_name,
      phone_number: applicant.phone_number,
      birthday: applicant.birthday,
      properties: applicant?.lead_card?.property_name,
      lead_card: applicant?.lead_card,
      pending_applications: applicant?.pending_applications,
    }));

  const COLUMNS = [
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.name'),
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: (a, b) => basicSort(a.full_name, b.full_name),
      // Default is sorting by name ascending.
      sortOrder: sortedInfo ? sortedInfo?.columnKey === 'full_name' && sortedInfo?.order : 'ascend',
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.phone'),
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: getTranslatedString('property'),
      dataIndex: 'properties',
      key: 'properties',
      sorter: (a, b) => basicSort(a.properties, b.properties),
      sortOrder: sortedInfo?.columnKey === 'properties' && sortedInfo?.order,
      filters: generateFiltersForGenericColumn(applicantsDataSource, 'properties'),
      onFilter: (value, record) => onFilterGenericColumn(value, record, 'properties'),
    },
  ];

  useEffect(() => {
    async function fetchApplicants() {
      const params = {};

      if (!values) {
        return;
      }

      if (!values.query && !values.site) {
        dispatch(['SET_FOUND_APPLICANTS', []]);
        return;
      }

      if (values.query) {
        params.query = values.query;
      }

      if (values.property === 'all') {
        params.propertyId = propertiesOptions.map((option) => extractIdFromValue(option.value));
      } else {
        params.propertyId = [extractIdFromValue(values.property)];
      }

      dispatch(['SET_FETCHING', true]);
      const results = await throttledSearchApplicants(params);
      dispatch(['SET_FOUND_APPLICANTS', results.leads]);
    }

    fetchApplicants();
  }, [values?.query, values?.site, values?.property]);

  function onChangeSite() {
    form.setFieldsValue({ property: 'all' });
  }

  function onSelectRow(_, selectedRows) {
    dispatch(['SET_SELECTED_APPLICANT', selectedRows[0]]);
  }

  async function onSubmit() {
    await form.validateFields();
    onSelect(selectedApplicant, values);
  }

  const handleTableChange = (_, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  return (
    <StyledForm
      layout="vertical"
      size={isMobile && 'large'}
      form={form}
      initialValues={{ property: 'all', ...initialValues }}
    >
      <Form.Item name="query">
        <Input suffix={<SearchOutlined />} />
      </Form.Item>
      <StyledSpace direction={isMobile ? 'vertical' : 'horizontal'} size={isMobile ? 16 : 10}>
        <Form.Item label={getTranslatedString('site')} name="site">
          <Select
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            options={sitesOptions}
            onChange={onChangeSite}
            placeholder={getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.selectASite'
            )}
          />
        </Form.Item>
        <Form.Item label={getTranslatedString('property')} name="property">
          <Select
            options={[ALL_OPTION, ...propertiesOptions]}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
      </StyledSpace>
      <Table
        dataSource={applicantsDataSource}
        columns={COLUMNS}
        onChange={handleTableChange}
        hideActionAndSearch
        loading={fetchingFoundApplicants}
        size={isMobile ? 'default' : 'middle'}
        rowSelection={{
          type: 'radio',
          fixed: true,
          onChange: onSelectRow,
        }}
        pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
        scroll={{
          x: isMobile,
        }}
      />
      {extraFormItems}
      <Space>
        <Button type="primary" htmlType="submit" disabled={!selectedApplicant} onClick={onSubmit}>
          {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.add')}
        </Button>
        <Button htmlType="button" onClick={onCancel}>
          {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.cancel')}
        </Button>
      </Space>
    </StyledForm>
  );
}

SearchApplicants.defaultProps = {
  siteAndProperties: [],
  initialValues: {},
};

SearchApplicants.propTypes = {
  mode: PropTypes.string,
  siteAndProperties: PropTypes.array,
  initialValues: PropTypes.any,
  extraFormItems: PropTypes.any,
  onSelect: PropTypes.func,
  onCancel: PropTypes.func,
};

export default SearchApplicants;
